import { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useUIStore } from '@/store/ui'
import { fetchCommonLibrary } from '@/api/commonLibrary'
import { fetchGame } from '@/api/games'
import { fetchGroupSummary } from '@/api/groups'
import { addToWishlist, promoteToPlaying, promoteToPlayed } from '@/api/wishlist'
import { toFriend } from '@/lib/steam'
import { Icon } from '@/components/ui/Icon'
import { Spinner } from '@/components/ui/Spinner'
import { Avatar } from '@/components/ui/Avatar'
import { GameCover } from '@/components/ui/GameCover'
import { RatingPill } from '@/components/ui/RatingPill'
import { TagPill } from '@/components/ui/TagPill'
import { ThemedSelect } from '@/components/ui/ThemedSelect'
import { CoopModesBadges, CoopPlayersBadge } from '@/components/ui/CoopBadges'
import { HltbBadge } from '@/components/ui/HltbBadge'
import { GroupHeader } from '@/components/groups/GroupHeader'
import { GameDetailModal } from '@/components/wishlist/GameDetailModal'
import { useGroupPermissions } from '@/hooks/useGroupPermissions'
import type { Friend, Game, SlimGame } from '@/types'

type SortKey = 'rating' | 'release' | 'random'

const SORT_OPTIONS: readonly { value: SortKey; label: string }[] = [
  { value: 'rating', label: 'Highest rated' },
  { value: 'release', label: 'Newest' },
  { value: 'random', label: 'Shuffle' },
]

interface Candidate {
  gameId: string
  ownerCount: number
  owners: Friend[]
  missing: Friend[]
  game: SlimGame
}

interface Section {
  count: number
  items: Candidate[]
}

interface ActedSets {
  wishlist: Set<string>
  playing: Set<string>
  played: Set<string>
}

const EMPTY_ACTED: ActedSets = {
  wishlist: new Set(),
  playing: new Set(),
  played: new Set(),
}

// Empty `currency` is the sentinel for "pricing not loaded yet" — the modal
// uses it to hide PriceTag while the full Game payload is in flight.
function slimToPartialGame(slim: SlimGame): Game {
  return {
    ...slim,
    appId: Number(slim.id),
    price: 0,
    sale: null,
    currency: '',
    publisher: '',
    desc: '',
  }
}

export default function CommonLibraryPage() {
  const { activeGroupId } = useUIStore()
  const { canEditContent } = useGroupPermissions()
  const queryClient = useQueryClient()

  const { data: summary } = useQuery({
    queryKey: ['group-summary', activeGroupId],
    queryFn: () => fetchGroupSummary(activeGroupId!),
    enabled: !!activeGroupId,
  })
  const friends: Friend[] = useMemo(
    () => (summary?.members ?? []).map(toFriend),
    [summary],
  )

  const { data, isLoading } = useQuery({
    queryKey: ['common-library', activeGroupId],
    queryFn: () => fetchCommonLibrary(activeGroupId!),
    enabled: !!activeGroupId,
  })

  // Ownership map (full union across every member's library).
  const ownership: Record<string, string[]> = useMemo(() => {
    const map: Record<string, string[]> = {}
    data?.ownership.forEach((o) => {
      map[o.gameId] = o.owners
    })
    return map
  }, [data])

  // Direct id→SlimGame index of everything we got metadata for.
  const gamesById = useMemo(() => {
    const map = new Map<string, SlimGame>()
    data?.candidates.forEach((c) => map.set(c.game.id, c.game))
    return map
  }, [data])

  const wishedSet = useMemo(() => new Set(data?.wishedGameIds ?? []), [data])
  const playingSet = useMemo(() => new Set(data?.playingGameIds ?? []), [data])
  const playedSet = useMemo(() => new Set(data?.playedGameIds ?? []), [data])

  // ── Filter / control state ─────────────────────────────────────────────
  const [includedMembers, setIncludedMembers] = useState<Set<string>>(() => new Set())
  const [threshold, setThreshold] = useState(1)
  const [q, setQ] = useState('')
  const [tagFilter, setTagFilter] = useState<string | null>(null)
  const [multiplayerOnly, setMultiplayerOnly] = useState(true)
  const [coopFitOnly, setCoopFitOnly] = useState(false)
  const [hideTriaged, setHideTriaged] = useState(true)
  const [sort, setSort] = useState<SortKey>('rating')
  const [randomSeed, setRandomSeed] = useState(0)
  const [acted, setActed] = useState<ActedSets>(EMPTY_ACTED)
  const [detailGameId, setDetailGameId] = useState<string | null>(null)

  const detailSlim = detailGameId ? gamesById.get(detailGameId) : undefined
  const { data: detailGame } = useQuery({
    queryKey: ['game', detailGameId],
    queryFn: () => fetchGame(detailGameId!),
    enabled: !!detailGameId,
    placeholderData: detailSlim ? slimToPartialGame(detailSlim) : undefined,
  })

  const prefetchGame = (gameId: string) => {
    queryClient.prefetchQuery({
      queryKey: ['game', gameId],
      queryFn: () => fetchGame(gameId),
      staleTime: 60_000,
    })
  }

  // `friends` gets a new reference on every group-summary refetch (because
  // mutations invalidate that query to refresh counts). The reset effect
  // below must NOT key off `friends` directly, or it wipes user state —
  // member selection, threshold, optimistic `acted` — on every click. We
  // pre-derive a stable signature over actual membership composition + the
  // set of non-limited members and only reset when *that* changes.
  const membershipSignature = useMemo(() => {
    const parts = friends
      .map((f) => `${f.id}:${f.status === 'limited' ? 'l' : 'a'}`)
      .sort()
    return parts.join(',')
  }, [friends])

  const friendsRef = useRef(friends)
  friendsRef.current = friends

  useEffect(() => {
    const fresh = new Set<string>()
    friendsRef.current.forEach((f) => {
      if (f.status !== 'limited') fresh.add(f.id)
    })
    setIncludedMembers(fresh)
    setThreshold(Math.max(1, fresh.size))
    setActed({ wishlist: new Set(), playing: new Set(), played: new Set() })
  }, [activeGroupId, membershipSignature])

  const includedArr = friends.filter((f) => includedMembers.has(f.id))
  const includedCount = includedArr.length

  // Clamp threshold when membership changes.
  useEffect(() => {
    if (includedCount === 0) return
    if (threshold > includedCount) setThreshold(includedCount)
    if (threshold < 1) setThreshold(1)
  }, [includedCount, threshold])

  const excludedLimited = friends.filter(
    (f) => f.status === 'limited' && !includedMembers.has(f.id),
  )

  // Defer everything that drives the heavy filter+sort below. The control
  // surface (search input, pills, threshold buttons) still reads the live
  // state for instant visual feedback, but the expensive memo runs against
  // these deferred values — so React keeps the previous list rendered while
  // the new one computes in the background. `isFiltering` (below) is true
  // whenever the deferred values haven't caught up yet; we show a spinner
  // in the toolbar so the user knows work is in flight.
  const dQ = useDeferredValue(q)
  const dTagFilter = useDeferredValue(tagFilter)
  const dMultiplayerOnly = useDeferredValue(multiplayerOnly)
  const dCoopFitOnly = useDeferredValue(coopFitOnly)
  const dHideTriaged = useDeferredValue(hideTriaged)
  const dSort = useDeferredValue(sort)
  const dRandomSeed = useDeferredValue(randomSeed)
  const dThreshold = useDeferredValue(threshold)
  const dIncludedMembers = useDeferredValue(includedMembers)

  const dIncludedArr = useMemo(
    () => friends.filter((f) => dIncludedMembers.has(f.id)),
    [friends, dIncludedMembers],
  )
  const dIncludedCount = dIncludedArr.length

  // ── Compute intersection candidates ────────────────────────────────────
  const allCandidates: Candidate[] = useMemo(() => {
    if (dIncludedCount === 0) return []
    const includedIds = new Set(dIncludedArr.map((f) => f.id))
    const candidates: Candidate[] = []
    for (const [gameId, ownerIds] of Object.entries(ownership)) {
      const game = gamesById.get(gameId)
      if (!game) continue
      const ownerSet = new Set(ownerIds)
      const owners: Friend[] = []
      const missing: Friend[] = []
      for (const f of dIncludedArr) {
        if (ownerSet.has(f.id) && includedIds.has(f.id)) owners.push(f)
        else missing.push(f)
      }
      if (owners.length >= dThreshold) {
        candidates.push({ gameId, ownerCount: owners.length, owners, missing, game })
      }
    }
    return candidates
  }, [ownership, gamesById, dIncludedCount, dThreshold, dIncludedArr])

  // ── Apply filters + sort ───────────────────────────────────────────────
  const groupSize = friends.length

  const filtered: Candidate[] = useMemo(() => {
    let list = allCandidates

    if (dQ) {
      const ql = dQ.toLowerCase()
      list = list.filter(
        (d) =>
          d.game.title.toLowerCase().includes(ql) ||
          d.game.tags.some((t) => t.toLowerCase().includes(ql)) ||
          d.game.developer.toLowerCase().includes(ql),
      )
    }
    if (dTagFilter) {
      list = list.filter((d) => d.game.tags.includes(dTagFilter))
    }
    if (dMultiplayerOnly) {
      list = list.filter((d) => d.game.multiplayer !== '')
    }
    if (dCoopFitOnly) {
      const N = groupSize
      list = list.filter((d) => {
        const c = d.game.coop
        return !!c && N >= c.min && (c.max === 0 || N <= c.max)
      })
    }
    if (dHideTriaged) {
      // Include the optimistic `acted` state so a freshly-clicked row
      // disappears immediately, before the server-side triage sets refetch.
      list = list.filter(
        (d) =>
          !wishedSet.has(d.gameId) &&
          !playingSet.has(d.gameId) &&
          !playedSet.has(d.gameId) &&
          !acted.wishlist.has(d.gameId) &&
          !acted.playing.has(d.gameId) &&
          !acted.played.has(d.gameId),
      )
    }

    if (dSort === 'rating') {
      list = [...list].sort((a, b) => b.game.rating - a.game.rating)
    } else if (dSort === 'release') {
      list = [...list].sort((a, b) => b.game.released.localeCompare(a.game.released))
    } else if (dSort === 'random') {
      const seeded = (id: string) => {
        let h = dRandomSeed + 5381
        for (let i = 0; i < id.length; i++) h = (h * 33 + id.charCodeAt(i)) >>> 0
        return h
      }
      list = [...list].sort((a, b) => seeded(a.gameId) - seeded(b.gameId))
    }
    return list
  }, [
    allCandidates,
    dQ,
    dTagFilter,
    dMultiplayerOnly,
    dCoopFitOnly,
    dHideTriaged,
    wishedSet,
    playingSet,
    playedSet,
    acted,
    dSort,
    dRandomSeed,
    groupSize,
  ])

  const isFiltering =
    dQ !== q ||
    dTagFilter !== tagFilter ||
    dMultiplayerOnly !== multiplayerOnly ||
    dCoopFitOnly !== coopFitOnly ||
    dHideTriaged !== hideTriaged ||
    dSort !== sort ||
    dRandomSeed !== randomSeed ||
    dThreshold !== threshold ||
    dIncludedMembers !== includedMembers

  // Tag rail — top tags across the candidate set we have metadata for.
  const candidateTags = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const c of allCandidates) {
      for (const t of c.game.tags) counts[t] = (counts[t] ?? 0) + 1
    }
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([t]) => t)
  }, [allCandidates])

  // ── Sections by owner count (locked "by owners" grouping) ─────────────
  const sections: Section[] = useMemo(() => {
    const buckets = new Map<number, Candidate[]>()
    for (const c of filtered) {
      const bucket = buckets.get(c.ownerCount) ?? []
      bucket.push(c)
      buckets.set(c.ownerCount, bucket)
    }
    return [...buckets.entries()]
      .sort((a, b) => b[0] - a[0])
      .map(([count, items]) => ({ count, items }))
  }, [filtered])

  // ── Stats ─────────────────────────────────────────────────────────────
  // Stats track the deferred candidate set so the counts shown in the toolbar
  // stay in sync with the visible list (rather than flickering ahead of it).
  const stats = useMemo(() => {
    const everyone =
      dIncludedCount > 0
        ? allCandidates.filter((c) => c.ownerCount === dIncludedCount).length
        : 0
    return { total: allCandidates.length, everyone }
  }, [allCandidates, dIncludedCount])

  // ── Mutations ─────────────────────────────────────────────────────────
  const markActed = (kind: keyof ActedSets, gameId: string) => {
    setActed((prev) => {
      const next = new Set(prev[kind])
      next.add(gameId)
      return { ...prev, [kind]: next }
    })
  }

  // The common-library response carries its own snapshot of which games are
  // already wished/playing/played — without invalidating it, the optimistic
  // `acted` set hides the row only until this component unmounts. After
  // navigating away and back, the stale snapshot brings the row back.
  const invalidateTriageState = () => {
    queryClient.invalidateQueries({ queryKey: ['common-library', activeGroupId] })
    queryClient.invalidateQueries({ queryKey: ['group-summary', activeGroupId] })
  }

  const wishlistMutation = useMutation({
    mutationFn: (gameId: string) => addToWishlist(activeGroupId!, gameId),
    onMutate: (gameId) => {
      markActed('wishlist', gameId)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['wishlist', activeGroupId] })
      invalidateTriageState()
    },
  })

  const playingMutation = useMutation({
    mutationFn: (gameId: string) => promoteToPlaying(activeGroupId!, gameId),
    onMutate: (gameId) => {
      markActed('playing', gameId)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['playing', activeGroupId] })
      invalidateTriageState()
    },
  })

  const playedMutation = useMutation({
    mutationFn: (gameId: string) => promoteToPlayed(activeGroupId!, gameId),
    onMutate: (gameId) => {
      markActed('played', gameId)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['played', activeGroupId] })
      invalidateTriageState()
    },
  })

  if (!activeGroupId) {
    return (
      <div className="cg-screen">
        <div className="cg-empty">Select or create a group to see its common library.</div>
      </div>
    )
  }

  return (
    <div className="cg-screen cg-cl">
      <GroupHeader sublabel="Common Library" />

      {/* ── Intersection controls ───────────────────────────────────── */}
      <div className="cg-cl-controls">
        <div className="cg-cl-control-row">
          <span className="cg-cl-control-label">Intersect across</span>
          <div className="cg-cl-members">
            {friends.map((f) => {
              const on = includedMembers.has(f.id)
              const isLimited = f.status === 'limited'
              return (
                <button
                  key={f.id}
                  type="button"
                  className="cg-cl-member-chip"
                  data-on={on}
                  data-limited={isLimited}
                  title={
                    isLimited
                      ? `${f.name} — private profile, we only see games shared with you`
                      : f.name
                  }
                  onClick={() => {
                    setIncludedMembers((prev) => {
                      const next = new Set(prev)
                      if (next.has(f.id)) next.delete(f.id)
                      else next.add(f.id)
                      return next
                    })
                  }}
                >
                  <Avatar friend={f} size={22} showRing={false} />
                  <span className="cg-cl-member-name">{f.nickname || f.name}</span>
                  {isLimited && <span className="cg-cl-member-tag">private</span>}
                  <span className="cg-cl-member-check" aria-hidden="true">
                    {on ? <Icon name="check" size={10} /> : null}
                  </span>
                </button>
              )
            })}
          </div>
        </div>

        <div className="cg-cl-control-row">
          <span className="cg-cl-control-label">Owned by at least</span>
          {includedCount > 0 ? (
            <div className="cg-segment cg-cl-threshold">
              {Array.from({ length: includedCount }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  type="button"
                  className="cg-seg-btn"
                  data-active={threshold === n}
                  onClick={() => setThreshold(n)}
                >
                  {n}
                </button>
              ))}
            </div>
          ) : (
            <span className="cg-cl-threshold-hint">Pick at least one member to intersect</span>
          )}
          {includedCount > 0 && (
            <span className="cg-cl-threshold-hint">
              of {includedCount} ·{' '}
              {threshold === includedCount ? (
                <strong style={{ color: 'var(--fg-0)' }}>everyone selected</strong>
              ) : (
                <>
                  show games {includedCount - threshold}{' '}
                  {includedCount - threshold === 1 ? 'person' : 'people'} can join
                </>
              )}
            </span>
          )}
        </div>

        {excludedLimited.length > 0 && (
          <div className="cg-cl-excluded">
            <span className="cg-cl-excluded-icon">
              <Icon name="eye-off" size={12} />
            </span>
            <span className="cg-cl-excluded-body">
              <strong>{excludedLimited.map((f) => f.nickname || f.name).join(' & ')}</strong>{' '}
              {excludedLimited.length === 1 ? 'has' : 'have'} a private Steam profile — excluded so
              the intersection only counts libraries we can fully see.
            </span>
            <button
              type="button"
              className="cg-btn ghost tiny"
              onClick={() => {
                setIncludedMembers((prev) => {
                  const next = new Set(prev)
                  excludedLimited.forEach((f) => next.add(f.id))
                  return next
                })
              }}
            >
              Include anyway
            </button>
          </div>
        )}
      </div>

      {/* ── Toolbar ─────────────────────────────────────────────────── */}
      <div className="cg-toolbar cg-cl-toolbar">
        <div className="cg-search">
          <Icon name="search" size={14} />
          <input
            placeholder="Search common library…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          {q && (
            <button className="cg-search-clear" onClick={() => setQ('')}>
              <Icon name="x" size={10} />
            </button>
          )}
        </div>
        <button
          type="button"
          className="cg-cl-pill"
          data-active={multiplayerOnly}
          onClick={() => setMultiplayerOnly((v) => !v)}
          title="Hide singleplayer-only games"
        >
          <Icon name="controller" size={12} />
          <span>Multiplayer only</span>
        </button>
        <button
          type="button"
          className="cg-cl-pill"
          data-active={coopFitOnly}
          onClick={() => setCoopFitOnly((v) => !v)}
          title={`Filter to games whose player count fits this group of ${groupSize}`}
        >
          <Icon name="friends" size={12} />
          <span>Fits {groupSize} players</span>
        </button>
        <button
          type="button"
          className="cg-cl-pill"
          data-active={hideTriaged}
          onClick={() => setHideTriaged((v) => !v)}
          title="Hide games already on the group's wishlist, playing, or played list"
        >
          <Icon name="eye-off" size={12} />
          <span>Hide already added</span>
        </button>
        <div className="cg-toolbar-spacer" />
        {isFiltering && (
          <span
            className="cg-cl-filtering"
            title="Recomputing — the controls reflect your latest change, results catching up"
            aria-live="polite"
          >
            <Spinner size={12} wrapClassName="" />
            <span>Filtering…</span>
          </span>
        )}
        <span className="cg-cl-stats">
          <span>
            <strong>{stats.everyone}</strong> owned by all
          </span>
          <span className="cg-cl-stat-divider">·</span>
          <span>
            <strong>{filtered.length}</strong> of {stats.total} after filters
          </span>
        </span>
        <ThemedSelect
          value={sort}
          options={SORT_OPTIONS}
          onChange={(next) => {
            setSort(next)
            if (next === 'random') setRandomSeed((s) => s + 1)
          }}
          ariaLabel="Sort common library"
        />
        {sort === 'random' && (
          <button
            type="button"
            className="cg-icon-btn"
            onClick={() => setRandomSeed((s) => s + 1)}
            title="Re-shuffle"
          >
            <Icon name="refresh" size={12} />
          </button>
        )}
      </div>

      {candidateTags.length > 0 && (
        <div className="cg-tag-rail">
          {candidateTags.map((t) => (
            <TagPill
              key={t}
              active={tagFilter === t}
              onClick={() => setTagFilter(tagFilter === t ? null : t)}
            >
              {t}
            </TagPill>
          ))}
        </div>
      )}

      {/* ── Results ─────────────────────────────────────────────────── */}
      {isLoading ? (
        <Spinner />
      ) : dIncludedCount === 0 ? (
        <div className="cg-empty">
          <p>Select at least one member above to compute an intersection.</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="cg-empty">
          <p>No games match these filters across the selected intersection.</p>
          <p style={{ fontSize: 11.5, color: 'var(--fg-3)', marginTop: 6 }}>
            Try lowering the threshold, clearing filters, or excluding a member who owns few of
            the group's games.
          </p>
        </div>
      ) : (
        <AnimatePresence initial={false}>
          {sections.map((sec) => (
            <motion.div
              key={sec.count}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ opacity: { duration: 0.18 } }}
            >
              <CLSection
                section={sec}
                includedCount={dIncludedCount}
                groupSize={groupSize}
                wishedSet={wishedSet}
                playingSet={playingSet}
                playedSet={playedSet}
                acted={acted}
                onAdd={(gid) => wishlistMutation.mutate(gid)}
                onStart={(gid) => playingMutation.mutate(gid)}
                onPlayed={(gid) => playedMutation.mutate(gid)}
                onOpenDetail={setDetailGameId}
                onPrefetch={prefetchGame}
                canEdit={canEditContent}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      )}

      {detailGameId && (
        detailGame ? (
          <GameDetailModal
            game={detailGame}
            groupSize={groupSize}
            onClose={() => setDetailGameId(null)}
          />
        ) : (
          <div className="cg-modal-backdrop" onClick={() => setDetailGameId(null)}>
            <Spinner />
          </div>
        )
      )}
    </div>
  )
}

// ── Section ──────────────────────────────────────────────────────────────
interface SectionProps {
  section: Section
  includedCount: number
  groupSize: number
  wishedSet: Set<string>
  playingSet: Set<string>
  playedSet: Set<string>
  acted: ActedSets
  onAdd: (gameId: string) => void
  onStart: (gameId: string) => void
  onPlayed: (gameId: string) => void
  onOpenDetail: (gameId: string) => void
  onPrefetch: (gameId: string) => void
  canEdit: boolean
}

function CLSection({
  section,
  includedCount,
  groupSize,
  wishedSet,
  playingSet,
  playedSet,
  acted,
  onAdd,
  onStart,
  onPlayed,
  onOpenDetail,
  onPrefetch,
  canEdit,
}: SectionProps) {
  const { count, items } = section
  const missing = includedCount - count
  let tier: 'all' | 'almost' | 'partial'
  let title: string
  let sub: string
  if (missing === 0) {
    tier = 'all'
    title = 'Everyone owns this'
    sub = `${items.length} ${items.length === 1 ? 'game' : 'games'} ready to launch tonight — no one has to buy anything.`
  } else if (missing === 1) {
    tier = 'almost'
    title = 'One person away'
    sub = `${items.length} ${items.length === 1 ? 'game' : 'games'} where ${missing} member is missing. A nudge or a gift bridges the gap.`
  } else {
    tier = 'partial'
    title = `${count} of ${includedCount} own these`
    sub = `${items.length} ${items.length === 1 ? 'game' : 'games'} owned by ${count} out of ${includedCount} selected members.`
  }

  return (
    <section className="cg-cl-section">
      <div className="cg-cl-section-head" data-tier={tier}>
        <div className="cg-cl-section-mark">
          <span className="cg-cl-section-num">{count}</span>
          <span className="cg-cl-section-of">/{includedCount}</span>
        </div>
        <div className="cg-cl-section-text">
          <div className="cg-cl-section-title">{title}</div>
          <div className="cg-cl-section-sub">{sub}</div>
        </div>
      </div>
      <div className="cg-cl-list">
        <AnimatePresence initial={false}>
          {items.map((d) => (
            <motion.div
              key={d.gameId}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, x: 24, transition: { duration: 0.22 } }}
              transition={{ opacity: { duration: 0.15 } }}
            >
              <CLRow
                data={d}
                includedCount={includedCount}
                groupSize={groupSize}
                wished={wishedSet.has(d.gameId)}
                playingNow={playingSet.has(d.gameId)}
                alreadyPlayed={playedSet.has(d.gameId)}
                acted={acted}
                onAdd={onAdd}
                onStart={onStart}
                onPlayed={onPlayed}
                onOpenDetail={onOpenDetail}
                onPrefetch={onPrefetch}
                canEdit={canEdit}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </section>
  )
}

// ── Row ──────────────────────────────────────────────────────────────────
interface RowProps {
  data: Candidate
  includedCount: number
  groupSize: number
  wished: boolean
  playingNow: boolean
  alreadyPlayed: boolean
  acted: ActedSets
  onAdd: (gameId: string) => void
  onStart: (gameId: string) => void
  onPlayed: (gameId: string) => void
  onOpenDetail: (gameId: string) => void
  onPrefetch: (gameId: string) => void
  canEdit: boolean
}

function CLRow({
  data,
  includedCount,
  groupSize,
  wished,
  playingNow,
  alreadyPlayed,
  acted,
  onAdd,
  onStart,
  onPlayed,
  onOpenDetail,
  onPrefetch,
  canEdit,
}: RowProps) {
  const { game, owners, missing, ownerCount } = data
  const isAll = ownerCount === includedCount
  return (
    <div
      className="cg-cl-row"
      data-all={isAll}
      onMouseEnter={() => onPrefetch(game.id)}
      onClick={() => onOpenDetail(game.id)}
    >
      <GameCover game={game} size="md" />
      <div className="cg-cl-row-main">
        <div className="cg-title-line">
          <span className="cg-title">{game.title}</span>
          {playingNow && (
            <span className="cg-cl-flag playing">
              <span className="cg-pulse-dot" />
              Playing
            </span>
          )}
          {alreadyPlayed && !playingNow && (
            <span className="cg-cl-flag played">
              <Icon name="star" size={9} /> Played
            </span>
          )}
          {wished && !playingNow && !alreadyPlayed && (
            <span className="cg-cl-flag wished">
              <Icon name="wishlist" size={9} /> Wishlisted
            </span>
          )}
        </div>
        <div className="cg-subtitle">
          <span className="cg-tags-inline">{game.tags.slice(0, 4).join(' · ')}</span>
        </div>
        <div className="cg-coop compact">
          <CoopPlayersBadge game={game} groupSize={groupSize} />
          <HltbBadge game={game} />
          <CoopModesBadges coop={game.coop} compact />
        </div>
      </div>
      <div className="cg-cl-owners">
        <div className="cg-cl-owner-row">
          {[...owners, ...missing].map((f) => (
            <Avatar
              key={f.id}
              friend={f}
              size={26}
              owns={owners.includes(f)}
              showRing
            />
          ))}
        </div>
        <div className="cg-cl-owner-meta">
          {isAll ? (
            <span className="cg-cl-owner-meta-all">All {includedCount} own it</span>
          ) : (
            <span>
              {ownerCount}/{includedCount} own ·{' '}
              <strong>{missing.map((f) => f.nickname || f.name).join(', ')}</strong> missing
            </span>
          )}
        </div>
      </div>
      <div className="cg-cl-rating">
        <RatingPill rating={game.rating} reviews={game.reviews} />
      </div>
      {canEdit && (
        <CLActions
          gameId={game.id}
          wished={wished}
          playingNow={playingNow}
          alreadyPlayed={alreadyPlayed}
          acted={acted}
          onAdd={onAdd}
          onStart={onStart}
          onPlayed={onPlayed}
        />
      )}
    </div>
  )
}

// ── Three-CTA cluster ────────────────────────────────────────────────────
interface ActionsProps {
  gameId: string
  wished: boolean
  playingNow: boolean
  alreadyPlayed: boolean
  acted: ActedSets
  onAdd: (gameId: string) => void
  onStart: (gameId: string) => void
  onPlayed: (gameId: string) => void
}

function CLActions({
  gameId,
  wished,
  playingNow,
  alreadyPlayed,
  acted,
  onAdd,
  onStart,
  onPlayed,
}: ActionsProps) {
  const wishlistOn = wished || acted.wishlist.has(gameId)
  const playingOn = playingNow || acted.playing.has(gameId)
  const playedOn = alreadyPlayed || acted.played.has(gameId)
  // A game lives in exactly one bucket at a time (server-side enforced via
  // promote/demote endpoints). Once a row's been triaged, lock the other
  // two CTAs so misclicks can't fire racing mutations.
  const anyActed = wishlistOn || playingOn || playedOn

  return (
    <div className="cg-cl-actions row" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className="cg-cl-cta"
        data-on={wishlistOn}
        disabled={anyActed}
        onClick={() => onAdd(gameId)}
        title={
          wishlistOn
            ? 'On the group wishlist'
            : playingOn
            ? 'Already moved to Now Playing'
            : playedOn
            ? 'Already marked as played'
            : 'Add to group wishlist'
        }
      >
        <Icon name={wishlistOn ? 'check' : 'wishlist'} size={11} />
        <span>{wishlistOn ? 'Wishlisted' : 'Wishlist'}</span>
      </button>
      <button
        type="button"
        className="cg-cl-cta playing"
        data-on={playingOn}
        disabled={anyActed}
        onClick={() => onStart(gameId)}
        title={
          playingOn
            ? 'Already in Now Playing'
            : wishlistOn
            ? 'On the group wishlist — promote from there'
            : playedOn
            ? 'Already marked as played'
            : 'Move to Now Playing'
        }
      >
        <Icon name={playingOn ? 'check' : 'controller'} size={11} />
        <span>{playingOn ? 'Playing' : 'Start playing'}</span>
      </button>
      <button
        type="button"
        className="cg-cl-cta played"
        data-on={playedOn}
        disabled={anyActed}
        onClick={() => onPlayed(gameId)}
        title={
          playedOn
            ? 'Already marked as played'
            : wishlistOn
            ? 'On the group wishlist — mark played from there'
            : playingOn
            ? 'Currently in Now Playing'
            : 'Mark as played by this group'
        }
      >
        <Icon name={playedOn ? 'check' : 'star'} size={11} />
        <span>{playedOn ? 'Played' : 'Mark played'}</span>
      </button>
    </div>
  )
}
